/**
 * The Gear catalogue product schema (v1).
 *
 * One JSON file per product under contracts/catalog/av/products/<type>/<brand>/<slug>.json.
 * Authoring rules: contracts/catalog/av/AUTHORING.md. Review: REVIEW.md.
 *
 * Conventions:
 * - Lengths are millimetres, mass kilograms, power watts (continuous), impedance ohms.
 * - Every spec value that a compatibility rule or the 3D model depends on is a
 *   `Sourced` value: the number plus the keys of the `sources` it came from.
 * - Absent optional fields mean "not published". Rules treat them as unknown,
 *   never as a pass.
 */
import { z } from 'zod';

export const SCHEMA_VERSION = 1 as const;

// ── Identity ────────────────────────────────────────────────────────────────

export const PRODUCT_TYPES = [
  'speaker_bookshelf',
  'speaker_tower',
  'speaker_center',
  'speaker_surround',
  'speaker_height',
  'speaker_in_ceiling',
  'subwoofer',
  'av_receiver',
  'av_processor',
  'power_amp',
  'tv',
  'projector',
  'projector_screen',
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const SPEAKER_TYPES = [
  'speaker_bookshelf',
  'speaker_tower',
  'speaker_center',
  'speaker_surround',
  'speaker_height',
  'speaker_in_ceiling',
] as const satisfies readonly ProductType[];
export type SpeakerType = (typeof SPEAKER_TYPES)[number];

export function isSpeakerType(t: ProductType): t is SpeakerType {
  return (SPEAKER_TYPES as readonly string[]).includes(t);
}

/** `brand-slug.model-slug`: lowercase letters, digits, hyphens; one dot. */
export const PRODUCT_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*\.[a-z0-9]+(?:-[a-z0-9]+)*$/;

// ── Provenance ──────────────────────────────────────────────────────────────

export const SOURCE_KINDS = [
  'manual', // owner's manual / installation guide (manufacturer)
  'spec_sheet', // manufacturer spec / info sheet PDF
  'drawing', // manufacturer dimensional drawing
  'product_page', // manufacturer product web page
  'retailer', // retailer page (e.g. Crutchfield) — cross-check only
  'measurement', // independent measurement (Stereophile, Audioholics, Erin's Audio Corner)
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];
export const MANUFACTURER_SOURCE_KINDS: readonly SourceKind[] = ['manual', 'spec_sheet', 'drawing', 'product_page'];

const sourceKey = z.string().regex(/^s[0-9]+$/, 'source keys are s1, s2, …');
const srcList = z.array(sourceKey).min(1, 'every sourced value needs at least one source');

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

export const sourceSchema = z.strictObject({
  url: z.url(),
  kind: z.enum(SOURCE_KINDS),
  retrieved: isoDate,
  /** Where in the source the value is (page, table, section). */
  locator: z.string().min(1).optional(),
});
export type Source = z.infer<typeof sourceSchema>;

function sourced<T extends z.ZodType>(value: T) {
  return z.strictObject({ v: value, src: srcList });
}

// 4 m: the widest products are 135"+ fixed-frame 16:9 screens (~3.1 m).
const mm = z.number().positive().max(4000);
const posNum = z.number().positive();

// ── Speakers ────────────────────────────────────────────────────────────────

export const DRIVER_ROLES = ['tweeter', 'midrange', 'woofer', 'coaxial', 'passive_radiator', 'upfiring'] as const;
export type DriverRole = (typeof DRIVER_ROLES)[number];

export const driverSpecSchema = z.strictObject({
  role: z.enum(DRIVER_ROLES),
  /** Nominal driver size as published (6.5" → 165). */
  nominalMm: mm,
  count: z.number().int().min(1).max(12),
});

/** `open_back`: in-wall / in-ceiling speakers that fire into the wall or ceiling cavity (no enclosure of their own).
 *  `side`: on-wall/bookshelf cabinets with the port on a left/right edge rather than front or rear
 *  (e.g. KEF Q4 Meta) — paired with a port feature on the recipe's `left` or `right` face. */
export const PORTS = ['front', 'rear', 'side', 'bottom', 'sealed', 'passive_radiator', 'open_back'] as const;
export type Port = (typeof PORTS)[number];

export const speakerSpecsSchema = z.strictObject({
  kind: z.literal('speaker'),
  /** Powered (built-in amplifier) speakers need a line-level source, not an amp channel. */
  active: sourced(z.boolean()),
  /** Nominal impedance, ohms. Required for passive speakers. */
  impedanceOhm: sourced(z.number().min(2).max(16)).optional(),
  /** Sensitivity in dB SPL at 1 m, with the drive reference the maker used.
   *  `inRoom`: the maker states an in-room / half-space figure (reads ~3 dB high). */
  sensitivityDb: z
    .strictObject({
      v: z.number().min(70).max(110),
      ref: z.enum(['2.83V', '1W', 'unstated']),
      inRoom: z.boolean(),
      src: srcList,
    })
    .optional(),
  /** Low end of the published frequency response, Hz. Required except for surround and
   *  height modules, which makers often certify to the Dolby Atmos spec instead of publishing a range. */
  lowHz: sourced(z.number().min(10).max(500)).optional(),
  /** Maker's recommended amplifier power range, W. */
  recAmpW: z.strictObject({ min: posNum, max: posNum, src: srcList }).optional(),
  drivers: sourced(z.array(driverSpecSchema).min(1)),
  port: sourced(z.enum(PORTS)),
});
export type SpeakerSpecs = z.infer<typeof speakerSpecsSchema>;

// ── Subwoofers ──────────────────────────────────────────────────────────────

export const subwooferSpecsSchema = z.strictObject({
  kind: z.literal('subwoofer'),
  /** Continuous (RMS) amplifier power, W. */
  ampW: sourced(posNum),
  drivers: sourced(z.array(driverSpecSchema).min(1)),
  port: sourced(z.enum(PORTS)),
  lowHz: sourced(z.number().min(5).max(100)),
  inputs: sourced(
    z.strictObject({
      /** Line-level LFE / sub input (RCA or XLR). */
      lineLevel: z.boolean(),
      /** Speaker-level (high-level) inputs. */
      highLevel: z.boolean(),
    }),
  ),
});
export type SubwooferSpecs = z.infer<typeof subwooferSpecsSchema>;

// ── Electronics ─────────────────────────────────────────────────────────────

/** Channel groups as receiver manuals name their terminals and pre-outs. */
/** Assignable pairs beyond the 7.x bed: `wide` (front wide), `height1`–`height4` in the order the maker numbers them. */
export const CHANNEL_GROUPS = ['front', 'center', 'surround', 'surround_back', 'wide', 'height1', 'height2', 'height3', 'height4'] as const;
export type ChannelGroup = (typeof CHANNEL_GROUPS)[number];

export const ampPowerSchema = z.strictObject({
  /** Rated continuous watts per channel into 8 Ω. */
  w8: posNum,
  /** Rated continuous watts per channel into 4 Ω, if published. */
  w4: posNum.optional(),
  /** Channels driven for the rating (2 for most receivers). */
  channelsDriven: z.number().int().min(1).max(16),
  band: z.enum(['20-20k', '1k']),
  thdPct: z.number().positive().max(10),
  src: srcList,
});
export type AmpPower = z.infer<typeof ampPowerSchema>;

export const LAYOUTS = ['2.0', '2.1', '3.1', '5.1', '5.1.2', '5.1.4', '7.1', '7.1.2', '7.1.4'] as const;
export type CatalogLayout = (typeof LAYOUTS)[number];

export const avSpecsSchema = z.strictObject({
  kind: z.literal('av'),
  /** Built-in amplifier channels. 0 for a processor. */
  ampChannels: sourced(z.number().int().min(0).max(16)),
  /** Main-zone speaker channels the unit processes (excluding subwoofer outputs). */
  processingChannels: sourced(z.number().int().min(2).max(16)),
  heightDecoding: sourced(z.strictObject({ atmos: z.boolean(), dtsx: z.boolean() })),
  /** Line-level pre-outs per channel group, and the number of subwoofer outputs. */
  preouts: sourced(
    z.strictObject({ groups: z.array(z.enum(CHANNEL_GROUPS)), subOuts: z.number().int().min(0).max(8) }),
  ),
  /** Required when ampChannels > 0. */
  power: ampPowerSchema.optional(),
  /** Lowest speaker impedance the manual allows (using the impedance setting if it has one), Ω.
   *  Required when ampChannels > 0. */
  minImpedanceOhm: sourced(z.number().min(2).max(8)).optional(),
  /** The speaker impedance setting's options, Ω, highest (default) first, e.g. [8, 6, 4].
   *  Absent when the unit has no such setting. */
  impedanceSettings: sourced(z.array(z.number().min(2).max(16)).min(2)).optional(),
  /** Layouts the manual lists for the main zone. Authoritative over channel arithmetic. */
  supportedLayouts: sourced(z.array(z.enum(LAYOUTS)).min(1)).optional(),
  roomCorrection: sourced(z.string().min(2)).optional(),
});
export type AvSpecs = z.infer<typeof avSpecsSchema>;

export const powerAmpSpecsSchema = z.strictObject({
  kind: z.literal('power_amp'),
  channels: sourced(z.number().int().min(1).max(16)),
  power: ampPowerSchema,
  minImpedanceOhm: sourced(z.number().min(2).max(8)),
  inputs: sourced(z.strictObject({ rca: z.boolean(), xlr: z.boolean() })),
});
export type PowerAmpSpecs = z.infer<typeof powerAmpSpecsSchema>;

// ── Displays (tv / projector / projector_screen) ───────────────────────────
//
// `body` (the top-level field, mm) means something slightly different per
// type — noted on each schema below — but is always what the recipe builds
// to and what the placement code uses for its footprint.

export const DISPLAY_RESOLUTIONS = ['1080p', '4K', '8K'] as const;
export type DisplayResolution = (typeof DISPLAY_RESOLUTIONS)[number];

/** `body`: overall cabinet W × H × D **without a stand** (wall-mount
 *  dimensions) — a TV is placed on the front wall, never on a stand. */
export const tvSpecsSchema = z.strictObject({
  kind: z.literal('tv'),
  /** Diagonal screen size, inches, as published. */
  screenIn: sourced(z.number().min(20).max(150)),
  resolution: sourced(z.enum(DISPLAY_RESOLUTIONS)),
  refreshHz: sourced(z.number().min(30).max(240)),
  hdmiInputs: sourced(z.number().int().min(0).max(8)),
  /** eARC on at least one HDMI input. */
  earc: sourced(z.boolean()),
});
export type TvSpecs = z.infer<typeof tvSpecsSchema>;

/** Distance-to-width throw ratio range. A fixed (non-zoom) lens has `min === max`. */
export const throwRatioSchema = z.strictObject({ min: posNum, max: posNum });
export type ThrowRatio = z.infer<typeof throwRatioSchema>;

/** `body`: overall chassis W × H × D **without the lens cap or mounting
 *  brackets** — PRJ-01 (av-compat) reads `throwRatio` against a screen's
 *  published width, not this body. */
export const projectorSpecsSchema = z.strictObject({
  kind: z.literal('projector'),
  lumensAnsi: sourced(z.number().min(100).max(10000)),
  resolution: sourced(z.enum(DISPLAY_RESOLUTIONS)),
  throwRatio: sourced(throwRatioSchema),
  hdmiInputs: sourced(z.number().int().min(0).max(8)),
});
export type ProjectorSpecs = z.infer<typeof projectorSpecsSchema>;

export const SCREEN_ASPECTS = ['16:9', '2.35:1'] as const;
export type ScreenAspect = (typeof SCREEN_ASPECTS)[number];
export const SCREEN_FORMATS = ['fixed', 'pull-down', 'motorized'] as const;
export type ScreenFormat = (typeof SCREEN_FORMATS)[number];

/** `body`: the outer frame or case as published (a fixed frame's outer
 *  frame; a pull-down or motorized screen's housing case). */
export const projectorScreenSpecsSchema = z.strictObject({
  kind: z.literal('projector_screen'),
  diagonalIn: sourced(z.number().min(60).max(200)),
  aspect: sourced(z.enum(SCREEN_ASPECTS)),
  gain: sourced(z.number().min(0.5).max(3)),
  format: sourced(z.enum(SCREEN_FORMATS)),
  acousticallyTransparent: sourced(z.boolean()),
});
export type ProjectorScreenSpecs = z.infer<typeof projectorScreenSpecsSchema>;

export const specsSchema = z.discriminatedUnion('kind', [
  speakerSpecsSchema,
  subwooferSpecsSchema,
  avSpecsSchema,
  powerAmpSpecsSchema,
  tvSpecsSchema,
  projectorSpecsSchema,
  projectorScreenSpecsSchema,
]);
export type ProductSpecs = z.infer<typeof specsSchema>;

/** Which specs kind each product type must carry. */
export const SPECS_KIND_FOR_TYPE: Record<ProductType, ProductSpecs['kind']> = {
  speaker_bookshelf: 'speaker',
  speaker_tower: 'speaker',
  speaker_center: 'speaker',
  speaker_surround: 'speaker',
  speaker_height: 'speaker',
  speaker_in_ceiling: 'speaker',
  subwoofer: 'subwoofer',
  av_receiver: 'av',
  av_processor: 'av',
  power_amp: 'power_amp',
  tv: 'tv',
  projector: 'projector',
  projector_screen: 'projector_screen',
};

// ── 3D recipe ───────────────────────────────────────────────────────────────
//
// Frame: millimetres. Origin at the bottom-centre of the body. +X right (as
// seen from the front), +Y up, +Z toward the listener (front face at z = +d/2).
// Features sit on a face and are placed with face coordinates:
//   u ∈ [-0.5, 0.5] across the face width (−0.5 = left edge as seen facing it)
//   v ∈ [0, 1] up the face height (0 = bottom edge)
// For the top face, u runs left→right and v runs back→front.

const hex = z.string().regex(/^#[0-9a-f]{6}$/, 'lowercase #rrggbb');
export const FINISHES = ['matte', 'satin', 'gloss', 'metal', 'wood'] as const;
export type Finish = (typeof FINISHES)[number];
const material = z.strictObject({ color: hex, finish: z.enum(FINISHES) });
export type RecipeMaterial = z.infer<typeof material>;

/** `slope` exists only on a `wedge` body: its sloped top. */
export const FACES = ['front', 'top', 'rear', 'left', 'right', 'slope'] as const;
export type Face = (typeof FACES)[number];
const u = z.number().min(-0.5).max(0.5);
const v = z.number().min(0).max(1);

export const recipeBodySchema = z.discriminatedUnion('shape', [
  /** A cabinet or chassis: box with rounded edges. `sideRadius` rounds only the
   *  four vertical edges (curved-side cabinets); `edgeRadius` rounds every edge. */
  z.strictObject({
    shape: z.literal('box'),
    w: mm,
    h: mm,
    d: mm,
    edgeRadius: z.number().min(0).max(100),
    sideRadius: z.number().min(0).max(300).optional(),
    /** Cabinet footprint above the feet when it is narrower than the published
     *  overall W × D (a tower on a wider plinth or outriggers). Centred. */
    cabinet: z.strictObject({ w: mm, d: mm }).optional(),
    material,
  }),
  /** A wedge: seen from the side, the top slopes from `frontH` at the front edge
   *  to `h` at the rear edge (Atmos elevation modules, angled on-wall surrounds).
   *  Features on the sloped top use face `slope`: u across the width, v from the
   *  front edge (0) up the slope to the rear edge (1). */
  z.strictObject({
    shape: z.literal('wedge'),
    w: mm,
    h: mm,
    d: mm,
    frontH: mm,
    material,
  }),
  /** A round in-ceiling speaker, modelled facing +Z like every other product
   *  (the scene turns it to face down): the visible trim disc (`trimMm` thick,
   *  `diameter` across) in front of the in-ceiling can. Product body is
   *  w = h = diameter, d = mounting depth. Features sit on the front (the
   *  grille-off baffle), with u/v over the disc's bounding square. */
  z.strictObject({
    shape: z.literal('round_in_ceiling'),
    diameter: mm,
    trimMm: z.number().min(1).max(40),
    d: mm,
    material,
  }),
]);
export type RecipeBody = z.infer<typeof recipeBodySchema>;

export const recipeFeatureSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('driver'),
    role: z.enum(DRIVER_ROLES),
    face: z.enum(FACES),
    u,
    v,
    /** Must equal the matching specs driver's nominalMm. */
    nominalMm: mm,
    /** Outer diameter of the visible frame/trim ring. >= nominalMm * 0.8. */
    outerMm: mm,
    style: z.enum(['cone', 'dome', 'horn', 'ribbon']),
    /** The diaphragm (cone or dome). */
    cone: material,
    /** The visible trim ring at the driver's outer edge (a thin band). */
    ring: material,
    /** Between diaphragm and trim ring: rubber surround, frame, or a horn's throat and flare.
     *  Defaults to near-black. */
    surround: material.optional(),
    /** style 'horn': the horn mouth around the tweeter (rectangular, or `round`
     *  with w = h for a circular horn on a round trim). style 'ribbon': the ribbon plate. */
    mouth: z.strictObject({ w: mm, h: mm, round: z.boolean().optional() }).optional(),
  }),
  z.strictObject({
    kind: z.literal('port'),
    face: z.enum(FACES),
    u,
    v,
    /** Round port diameter, or slot port width × height. */
    diameterMm: mm.optional(),
    slot: z.strictObject({ w: mm, h: mm }).optional(),
  }),
  /** A raised or recessed rectangle on a face: display window, trim plate, baffle panel. */
  z.strictObject({
    kind: z.literal('panel'),
    face: z.enum(FACES),
    u,
    v,
    w: mm,
    h: mm,
    /** + stands proud of the face, − is recessed into it. |depth| ≤ 15. */
    depthMm: z.number().min(-15).max(15),
    radiusMm: z.number().min(0).max(100).default(0),
    material,
  }),
  z.strictObject({
    kind: z.literal('knob'),
    face: z.enum(FACES),
    u,
    v,
    diameterMm: z.number().min(3).max(120),
    depthMm: z.number().min(1).max(60),
    material,
    /** Repeat along +u: `count` copies `pitchMm` apart (button rows). */
    repeat: z.strictObject({ count: z.number().int().min(2).max(24), pitchMm: posNum }).optional(),
  }),
  /** A projector's lens: a cylindrical barrel with a glass front, protruding
   *  from the face it sits on (almost always `front`). Protrusion is
   *  accounted for like any other feature, so `Box3` stays exactly `body`. */
  z.strictObject({
    kind: z.literal('lens'),
    face: z.enum(FACES),
    u,
    v,
    diameterMm: z.number().min(10).max(300),
    /** How far the barrel stands proud of the face, mm. 0 is a flush lens. */
    protrudeMm: z.number().min(0).max(150),
    barrel: material,
    glass: material,
  }),
  /** Feet or a plinth under the body. Included in body height (body.h is the overall height). */
  z.strictObject({
    kind: z.literal('feet'),
    style: z.enum(['pads', 'plinth', 'outriggers']),
    heightMm: z.number().min(1).max(120),
    material,
    /** Plinth / outrigger footprint; defaults to the body footprint. */
    w: mm.optional(),
    d: mm.optional(),
  }),
]);
export type RecipeFeature = z.infer<typeof recipeFeatureSchema>;

export const recipeSchema = z.strictObject({
  body: recipeBodySchema,
  features: z.array(recipeFeatureSchema).max(60),
});
export type Recipe = z.infer<typeof recipeSchema>;

// ── Product ─────────────────────────────────────────────────────────────────

export const reviewSchema = z.strictObject({
  author: z.string().min(1),
  authoredAt: isoDate,
  reviewer: z.string().min(1).optional(),
  reviewedAt: isoDate.optional(),
  verdict: z.enum(['pending', 'pass', 'fail']),
  /** Required when verdict is 'fail': what is wrong. */
  notes: z.string().min(1).optional(),
});

export const productSchema = z
  .strictObject({
    schemaVersion: z.literal(SCHEMA_VERSION),
    id: z.string().regex(PRODUCT_ID),
    brand: z.string().min(1),
    model: z.string().min(1),
    /** Product family for timbre matching (e.g. "Reference Premiere II"). Null when the model stands alone. */
    series: z.string().min(1).nullable(),
    /** Other names people search for: regional SKUs, short forms ("X3800H"). */
    aliases: z.array(z.string().min(1)),
    type: z.enum(PRODUCT_TYPES),
    sellUnit: z.enum(['pair', 'each']),
    status: z.enum(['current', 'discontinued']),
    msrpUsd: z.strictObject({ v: posNum, asOf: z.string().regex(/^\d{4}-\d{2}$/), src: srcList }),
    /** Amazon US ASIN, only when verified to be this exact product. */
    asin: z.string().regex(/^[A-Z0-9]{10}$/).nullable(),
    /** Overall dimensions as published (W × H × D), including feet and fixed parts. */
    body: z.strictObject({ w: mm, h: mm, d: mm, src: srcList }),
    weightKg: sourced(z.number().positive().max(200)).optional(),
    specs: specsSchema,
    sources: z.record(sourceKey, sourceSchema),
    recipe: recipeSchema,
    review: reviewSchema,
  })
  .superRefine((p, ctx) => {
    const issue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });

    if (SPECS_KIND_FOR_TYPE[p.type] !== p.specs.kind) {
      issue(['specs', 'kind'], `type ${p.type} requires specs.kind "${SPECS_KIND_FOR_TYPE[p.type]}"`);
    }
    if (p.specs.kind === 'speaker' && !p.specs.active.v && !p.specs.impedanceOhm) {
      issue(['specs', 'impedanceOhm'], 'passive speakers require impedanceOhm');
    }
    if (p.specs.kind === 'speaker' && !p.specs.lowHz && p.type !== 'speaker_surround' && p.type !== 'speaker_height') {
      issue(['specs', 'lowHz'], `${p.type} requires lowHz`);
    }
    if (p.specs.kind === 'av') {
      if (p.type === 'av_processor' && p.specs.ampChannels.v !== 0) {
        issue(['specs', 'ampChannels'], 'a processor has 0 amplifier channels');
      }
      if (p.specs.ampChannels.v > 0 && (!p.specs.power || !p.specs.minImpedanceOhm)) {
        issue(['specs'], 'amplified units require power and minImpedanceOhm');
      }
    }
    if (p.specs.kind === 'projector' && p.specs.throwRatio.v.min > p.specs.throwRatio.v.max) {
      issue(['specs', 'throwRatio'], 'throwRatio.min must not exceed throwRatio.max');
    }
    if (p.review.verdict === 'fail' && !p.review.notes) issue(['review', 'notes'], 'a failed review needs notes');
    if (p.review.verdict !== 'pending' && (!p.review.reviewer || !p.review.reviewedAt)) {
      issue(['review'], 'a reviewed product needs reviewer and reviewedAt');
    }
    if (p.review.reviewer && p.review.reviewer === p.review.author) {
      issue(['review', 'reviewer'], 'the reviewer must not be the author');
    }
  });
export type Product = z.infer<typeof productSchema>;
